import { createApi } from '@reduxjs/toolkit/query/react'
import { createNeighborhoodAwareBaseQuery } from '../../../Utils/baseQuery'

// This API handles the content-safety REPORTS queue — the list of things
// residents (or vendors) flagged with the in-app "Report" button: forum posts,
// comments, chat messages, profiles. Admins review this queue and either delete
// the offending content or dismiss the report as not-a-problem.
//
// It uses the SAME neighborhood-aware base query as the Forum admin screens, so
// the X-Neighborhood-ID header rides along automatically and the queue is scoped
// to whichever neighborhood the admin is currently viewing.
export const reportApiService = createApi({
  reducerPath: 'reportApi',
  baseQuery: createNeighborhoodAwareBaseQuery('/report/'),
  tagTypes: ['Report'],
  endpoints: (builder) => ({
    // Get the paginated queue of reported content. By default the backend
    // returns actionable/pending reports (the stuff an admin still has to look
    // at) — pass an explicit `status` to filter to a single state.
    //
    // Each row already carries everything the admin needs to act WITHOUT a
    // second lookup: who reported it, what kind of thing it is, a snippet of the
    // reported content plus its author, the reason, the date, and the status.
    // Params are page / rowsPerPage / status to match the backend contract.
    getReportsQueue: builder.query({
      query: (args) => ({
        url: 'admin/list',
        method: 'GET',
        params: args,
      }),
      providesTags: ['Report'],
      keepUnusedDataFor: 0,
    }),

    // Resolve one report. The admin either:
    //   - "Delete content" → { action: 'resolved', deleteContent: true }
    //     (backend soft-deletes the underlying post/comment, then closes the report)
    //   - "Dismiss"        → { action: 'dismissed', deleteContent: false }
    //     (the report was reviewed and there's nothing to remove)
    // Matches backend contract: POST /report/admin/:id/resolve
    resolveReport: builder.mutation({
      query: ({ id, action, deleteContent }) => ({
        url: `admin/${id}/resolve`,
        method: 'POST',
        body: { action, deleteContent },
      }),
      // Refresh the queue so the resolved report drops off the pending list.
      invalidatesTags: ['Report'],
    }),
  }),
})

export const {
  useGetReportsQueueQuery,
  useResolveReportMutation,
} = reportApiService
